/*
 * -----------------------------------------------------------------------
 *  <copyright file="add-organization-role.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox } from '@progress/kendo-react-inputs';
import { useBoolean, useTranslation } from 'hooks';
import { GET_ORGANISATION_MEMBERS_AND_ROLES, SAVE_ORGANISATION_ROLE } from 'queries/uam';
import Constants from 'utils/constants';
import { validateFields } from 'utils/validation';
import { SaveButton } from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import TextField from 'views/components/core/text-field';

interface InitialState {
    id: string;
    name: string;
    description: string;
    active: boolean;
}

const newRole = {
    id: null as any,
    name: '',
    description: '',
    active: false,
};

const normalizeRole = (r: InitialState): InitialState => ({
    ...r,
    name: r.name.trim(),
    description: r.description.trim(),
});

const AddOrganizationRole = ({ roles, organisationId, open, currentRole, onClose }: any) => {
    const { t } = useTranslation();
    const usersExist = useBoolean(false);
    const [role, setRole] = useState(newRole);
    const [errors, setErrors] = useState({} as any);
    const disabledSave = useBoolean(true);
    const saving = useBoolean(false);
    const [saveOrganizationRole] = useMutation(SAVE_ORGANISATION_ROLE);
    const initialValue: InitialState | undefined = currentRole && {
        id: currentRole.id,
        name: currentRole.name,
        description: currentRole.description,
        active: currentRole.active,
    };

    const isDirty = initialValue
        ? JSON.stringify(normalizeRole(initialValue)) !== JSON.stringify(normalizeRole(role))
        : disabledSave;

    useEffect(() => {
        if (open) {
            setRole({
                id: currentRole ? currentRole.id || role.id : role.id,
                name: currentRole ? currentRole.name || role.name : role.name,
                description: currentRole ? currentRole.description || role.description : role.description,
                active: currentRole ? currentRole.active : role.active,
            });
            if (currentRole) usersExist.setValue(!!currentRole.usersAmount);
        }
    }, [currentRole, open]);

    const close = (action: string | boolean | undefined) => {
        if (action !== 'error') {
            setRole(newRole);
            setErrors({});
            disabledSave.setTrue();
        }
        onClose(action);
    };

    const saveRole = () => {
        saving.setTrue();

        let rights = null;
        if (currentRole?.rights) {
            rights = currentRole.rights.map((right: any) => {
                return { ...right };
            });

            rights.forEach((el: any) => {
                if (el.__typename) delete el.__typename;
            });
        }

        const possibleRights = Constants.possiblePermissions.map((item: any) => ({
            active: true,
            moduleId: item.moduleId,
            permissions: item.permissions,
            element: item.element,
        }));

        saveOrganizationRole({
            variables: {
                organisationRole: {
                    ...role,
                    modules: currentRole?.modules,
                    rights: rights ?? possibleRights,
                },
            },
            refetchQueries: [
                {
                    query: GET_ORGANISATION_MEMBERS_AND_ROLES,
                },
            ],
        })
            .then(() => {
                saving.setFalse();
                close(currentRole ? 'update' : 'add');
            })
            .catch(() => {
                saving.setFalse();
                close('error');
            });
    };

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        // Remove the current role from a list of roles to check for uniqueness
        roles = roles.filter(
            (roleName: any) => roleName.name.toLowerCase().trim() !== currentRole?.name.toLowerCase().trim(),
        );
        if (
            field === 'name' &&
            roles.find((eachRole: any) => eachRole.name.toLowerCase().trim() === value?.toLowerCase().trim())
        ) {
            setErrors({
                ...errors,
                name: t('admin:uniqTitleError', { name: 'Role' }),
            });
        } else {
            const error = validateFields(value, field, length, required);
            setErrors({
                ...errors,
                ...error,
            });
        }
    };

    const onChange = (name: string) => (event: any) => {
        disabledSave.setFalse();
        let length = 150;

        if (name === 'name') {
            length = 50;
        }

        setRole({
            ...role,
            [name]: event.target.value,
        });
        validate(event.target.value, name, length, false);
    };

    return (
        <CustomDialog onClose={() => close(false)} fullWidth scroll="paper" maxWidth="md" open={open}>
            <DialogTitle id="add-role">
                {currentRole ? `${t('admin:editRole')} : ${currentRole.recordId}` : t('uam:addRole')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => close(false)}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="add-risk-dialog-content">
                <span className="inline-cell-checkbox ">
                    <Checkbox
                        rounded="none"
                        id="status-role-check"
                        className="inline-cell-checkbox k-display-block"
                        checked={role.active}
                        onChange={() => {
                            if (!usersExist.value || !currentRole?.active) {
                                if (role.name) disabledSave.setFalse();
                                setRole({
                                    ...role,
                                    active: !role.active,
                                });
                            } else {
                                onClose('has-users');
                            }
                        }}
                        label={t('common:active')}
                        data-testid="status-role-check"
                        size="medium"
                    />
                </span>
                <TextField
                    id="role-title"
                    label={`${t('role')}*`}
                    value={role.name}
                    fullWidth
                    error={!!errors.name}
                    placeholder={`${t('admin:enterRole')}*`}
                    variant="outlined"
                    onChange={onChange('name')}
                    helperText={
                        <CharactersRemaining
                            id="role-title-text"
                            length={role.name.length}
                            max={50}
                            error={errors.name}
                        />
                    }
                />
                <TextField
                    id="role-description"
                    placeholder={t('admin:enterDescription')}
                    autoFocus
                    multiline
                    rows={5}
                    fullWidth
                    label={t('common:description')}
                    margin="normal"
                    variant="outlined"
                    value={role.description}
                    onChange={onChange('description')}
                    helperText={
                        <CharactersRemaining
                            id="role-description-text"
                            length={role.description ? role.description.length : 0}
                            max={150}
                            error={errors.description}
                        />
                    }
                />
            </DialogContent>
            <DialogActions
                className={`actions-info-wrapper-edit-cv actions-add-risk assign-items ${
                    !currentRole ? 'flex-end' : ''
                }`}
            >
                <ModalUpdatedInfo item={currentRole} />
                <SaveButton
                    disabled={
                        Object.keys(errors).filter((item) => errors[item]).length > 0 ||
                        disabledSave.value ||
                        saving.value ||
                        !isDirty
                    }
                    text={currentRole ? 'update' : 'add'}
                    onClick={() => {
                        const error = validateFields(role.name, 'name', 50, true);
                        setErrors({
                            ...errors,
                            ...error,
                        });

                        if (!error.name) saveRole();
                    }}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default AddOrganizationRole;
